import React from "react";
import { Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CommonTable, { HeadCell } from "../../../../../business-types/grocery/components/CommonTable";
import { User } from "src/shared/types";
import { getPhoneNumber } from "src/shared/utils/phoneUtils";

export interface UserTableRow {
  id: string;
  sno: number;
  name: string;
  username: string;
  phones: string;
  joined: string;
  leaving: string;
  status: string;
  branch: string;
  role: string;
  Actions: React.ReactNode;
}

// Date formatting function - fixed to show dd-mm-yyyy format
const formatDate = (dateString: string): string => {
  if (!dateString || dateString === "N/A" || dateString.trim() === "") return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    return "N/A";
  }
};

/* ---------------------------------- */
/* Table Headers */
/* ---------------------------------- */
const headCells: HeadCell<UserTableRow>[] = [
  { id: "sno", label: "S.No", disablePadding: true },
  { id: "name", label: "Name" },
  { id: "username", label: "Username" },
  { id: "phones", label: "Phone number" },
  { id: "joined", label: "Date of joining" },
  { id: "leaving", label: "Date of leaving" },
  { id: "status", label: "Status" },
  { id: "branch", label: "Selected branch" },
  { id: "role", label: "Roles" },
  { id: "Actions", label: "Actions", disablePadding: true, align: "center" },
];

/* ---------------------------------- */
/* Helpers */
/* ---------------------------------- */
const getStatusText = (item: User) => {
  if (item?.dateOfLeaving) return "🟥 Deleted";

  const status = item?.status?.toLowerCase();

  if (status === "active") return "🟢 Active";
  if (status === "inactive") return "🟠 Inactive";

  return "N/A";
};

const isUserDeleted = (item: User): boolean => {
  return Boolean(item?.dateOfLeaving);
};

interface UserTableProps {
  filteredData: User[];
  onEditUser: (id: string) => void;
  onAddCardClick: (id: string) => void;
  isBranchSelected: boolean;
  onDeleteClick: (id: string, name?: string) => void;
  showDeletedUsers: boolean;
}

const UserTable: React.FC<UserTableProps> = ({ filteredData = [] as User[], onEditUser, onAddCardClick, isBranchSelected, onDeleteClick, showDeletedUsers }) => {
  const tableData = filteredData.map((item, index) => {
    const isDeleted = isUserDeleted(item);

    return {
      id: item._id.$oid,
      sno: index + 1,
      name: `${item.firstName ?? ""} ${item.middleName ?? ""} ${item.lastName ?? ""}`.trim(),
      username: item.userName || "N/A",
      phones: getPhoneNumber(item),
      joined: item.dateOfJoining || "",
      leaving: formatDate(item.dateOfLeaving || ""),
      status: getStatusText(item),
      branch: item.branchName || "",
      role: Array.isArray(item.roles) ? item.roles.join(", ") : item.roles || "",
      Actions: (
        <>
          {item.userName && !item.roles?.includes("Owner") && (
            <>
              <Tooltip title={isDeleted ? "Cannot edit deleted user" : "Edit User"} arrow>
                <span>
                  <IconButton
                    onClick={() => !isDeleted && onEditUser(item._id.$oid)}
                    disabled={isDeleted}
                    sx={{
                      color: isDeleted ? "gray" : "black",
                      "&.Mui-disabled": {
                        color: "gray",
                        opacity: 0.5,
                      },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={isDeleted ? "User already deleted" : "Delete User"} arrow>
                <span>
                  <IconButton
                    onClick={() => !isDeleted && onDeleteClick(item._id.$oid, item.firstName)}
                    disabled={isDeleted}
                    sx={{
                      color: isDeleted ? "gray" : "red",
                      "&.Mui-disabled": {
                        color: "gray",
                        opacity: 0.5,
                      },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {/* {renderBranchIcon({
            item,
            isBranchSelected,
            onAddCardClick,
            isDeleted,
          })} */}
        </>
      ),
    };
  });

  return (
    <div className="w-[96%] shadow-inner rounded-lg p-1 bg-white overflow-x-auto">
      <CommonTable title="" showToolbar={false} data={tableData} headCells={headCells} selectable={false} className="w-full" />
    </div>
  );
};

export default UserTable;
